import { Award, Briefcase, Lock, ShieldCheck, Star, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { findTrade } from "@/lib/constants/markets";
import { resolveMediaUrl } from "@/lib/utils";
import {
	getPublicContractorProfile,
	getPublicContractorProfileProject,
	type PublicContractorProfileResponse,
	type PublicContractorProjectResponse,
} from "../services/contractor-profile-api.service";

function ProjectDetailsModal({
	data,
	onClose,
	onRequireAuth,
}: {
	data: PublicContractorProjectResponse;
	onClose: () => void;
	onRequireAuth: () => void;
}): React.ReactElement {
	const { t } = useTranslation();
	const project = data.project;
	const imageUrls = project.imageUrls || [];
	const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-md">
			<button
				type="button"
				aria-label="Close project details"
				className="absolute inset-0 cursor-default"
				onClick={onClose}
			/>
			<Card
				className="relative max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-3xl border-0 bg-dashboard-paper shadow-2xl"
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header Image */}
				<div className="relative h-64 overflow-hidden sm:h-80">
					<img
						src={resolveMediaUrl(project.thumbnailUrl)}
						alt={project.title}
						className="h-full w-full object-cover"
					/>
					<div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
					<div className="absolute bottom-0 left-0 right-0 p-6">
						<CardTitle className="text-2xl font-bold text-dashboard-on-fill sm:text-3xl">
							{project.title}
						</CardTitle>
						<CardDescription className="mt-2 text-base text-dashboard-on-fill/80">
							{project.shortDescription}
						</CardDescription>
					</div>
					<button
						type="button"
						aria-label="Close"
						onClick={onClose}
						className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/30 text-dashboard-on-fill backdrop-blur-sm transition-colors hover:bg-black/50"
					>
						<svg
							className="h-5 w-5"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<title>Close</title>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				<CardContent className="space-y-6 p-6">
					{data.previewOnly ? (
						<div className="space-y-5 rounded-2xl bg-gradient-to-br from-dashboard-surface to-dashboard-divider p-5 sm:p-8 text-center">
							<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-dashboard-accent to-dashboard-accent-hover text-dashboard-on-fill shadow-lg shadow-dashboard-warm-200 dark:shadow-dashboard-warm-200/40">
								<Lock className="h-7 w-7" />
							</div>
							<div>
								<p className="text-lg font-semibold text-dashboard-text">
									Sign up to view full project details
								</p>
								<p className="mt-2 text-sm text-dashboard-muted">
									All project images and detailed information are available
									after creating an account.
								</p>
							</div>
							<div className="flex flex-wrap justify-center gap-3 pt-2">
								<Button
									className="bg-dashboard-accent hover:bg-dashboard-accent-hover text-dashboard-on-fill"
									onClick={onRequireAuth}
								>
									Create Account
								</Button>
								<Button
									variant="outline"
									className="border-dashboard-border"
									onClick={onRequireAuth}
								>
									Log in
								</Button>
							</div>
						</div>
					) : (
						<div>
							<h4 className="mb-4 text-sm font-semibold uppercase tracking-widest text-dashboard-placeholder">
								Project Gallery
							</h4>
							<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
								{imageUrls.map((url, index) => (
									<button
										type="button"
										key={url}
										aria-label={t("workPhotos.open", {
											index: index + 1,
											total: imageUrls.length,
										})}
										onClick={() => setLightboxIndex(index)}
										className="group cursor-zoom-in overflow-hidden rounded-xl border-0 bg-transparent p-0"
									>
										<img
											src={resolveMediaUrl(url)}
											alt={project.title}
											className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-110"
										/>
									</button>
								))}
							</div>
						</div>
					)}

					<div className="flex justify-end border-t border-dashboard-border-soft pt-4">
						<Button
							variant="ghost"
							className="text-dashboard-muted hover:text-dashboard-text"
							onClick={onClose}
						>
							Close
						</Button>
					</div>
				</CardContent>
			</Card>
			<ImageLightbox
				images={imageUrls.map((src, index) => ({
					src,
					alt: t("workPhotos.alt", { name: project.title, index: index + 1 }),
				}))}
				openIndex={lightboxIndex}
				onOpenIndexChange={setLightboxIndex}
			/>
		</div>
	);
}

export function PublicContractorProfilePage(): React.ReactElement {
	const { slug = "" } = useParams();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { isAuthenticated } = useAuthStore();

	const [loading, setLoading] = useState(true);
	const [profile, setProfile] =
		useState<PublicContractorProfileResponse | null>(null);
	const [selected, setSelected] =
		useState<PublicContractorProjectResponse | null>(null);
	const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
		null,
	);

	const requestedProjectId = useMemo(
		() => searchParams.get("project"),
		[searchParams],
	);

	useEffect(() => {
		async function load() {
			setLoading(true);
			const response = await getPublicContractorProfile(slug);
			setProfile(response);
			setLoading(false);
		}

		if (slug) {
			void load();
		}
	}, [slug]);

	useEffect(() => {
		async function loadProject(projectId: string) {
			const response = await getPublicContractorProfileProject(slug, projectId);
			if (response) {
				setSelected(response);
				setSelectedProjectId(projectId);
			}
		}

		if (requestedProjectId && profile) {
			void loadProject(requestedProjectId);
		}
	}, [requestedProjectId, profile, slug]);

	async function openProject(projectId: string) {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set("project", projectId);
			return next;
		});

		const response = await getPublicContractorProfileProject(slug, projectId);

		if (response) {
			setSelected(response);
			setSelectedProjectId(projectId);
		}
	}

	function closeModal() {
		setSelected(null);
		setSelectedProjectId(null);
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.delete("project");
			return next;
		});
	}

	function requireAuth() {
		const projectId = selectedProjectId || requestedProjectId;
		const redirectTo = `/contractor-profile/${slug}${projectId ? `?project=${projectId}` : ""}`;
		navigate("/login", { state: { redirectTo } });
	}

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-b from-dashboard-surface to-dashboard-paper p-4 sm:p-6">
				<div className="mx-auto max-w-7xl space-y-6">
					{/* Header Skeleton */}
					<div className="overflow-hidden rounded-2xl bg-gradient-to-br from-dashboard-dark to-dashboard-dark-elevated p-5 sm:p-8">
						<div className="flex items-start gap-6">
							<Skeleton className="h-24 w-24 rounded-2xl bg-dashboard-on-fill/20" />
							<div className="flex-1 space-y-4">
								<Skeleton className="h-6 w-32 rounded-full bg-dashboard-on-fill/20" />
								<Skeleton className="h-10 w-2/3 rounded-lg bg-dashboard-on-fill/20" />
								<Skeleton className="h-5 w-full rounded-lg bg-dashboard-on-fill/20" />
								<Skeleton className="h-5 w-3/4 rounded-lg bg-dashboard-on-fill/20" />
							</div>
						</div>
					</div>
					{/* Content Skeleton */}
					<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
						<Skeleton className="h-80 rounded-2xl" />
						<Skeleton className="h-80 rounded-2xl" />
						<Skeleton className="h-80 rounded-2xl" />
					</div>
				</div>
			</div>
		);
	}

	if (!profile) {
		return (
			<div className="flex min-h-[70vh] items-center justify-center p-6 bg-gradient-to-b from-dashboard-surface to-dashboard-paper">
				<Card className="w-full max-w-md rounded-2xl border-0 bg-dashboard-paper shadow-xl shadow-dashboard-border/50 dark:shadow-dashboard-shadow-ink/40">
					<CardHeader className="items-center text-center">
						<div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-dashboard-pill-risk-bg">
							<ShieldCheck className="h-8 w-8 text-dashboard-pill-risk" />
						</div>
						<CardTitle className="text-xl font-bold text-dashboard-text">
							Profile Not Found or Error
						</CardTitle>
						<CardDescription className="mt-2 text-sm text-dashboard-muted">
							We couldn't retrieve the contractor profile. The link may be
							invalid, the profile may not be published, or there was a
							connection issue.
						</CardDescription>
					</CardHeader>
					<CardContent className="flex flex-col gap-2 p-6 pt-0">
						<Button
							onClick={() => {
								setLoading(true);
								getPublicContractorProfile(slug).then((res) => {
									setProfile(res);
									setLoading(false);
								});
							}}
							className="w-full bg-dashboard-accent hover:bg-dashboard-accent-hover text-dashboard-on-fill font-medium py-2 rounded-xl"
						>
							Retry
						</Button>
						<Button
							variant="outline"
							onClick={() => navigate(-1)}
							className="w-full"
						>
							Go Back
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-b from-dashboard-surface to-dashboard-paper">
			{/* Hero Header Section */}
			<div className="relative overflow-hidden">
				{/* Decorative Background Elements */}
				<div className="absolute inset-0 bg-gradient-to-br from-dashboard-dark via-dashboard-dark-elevated to-dashboard-dark" />
				<div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDUpIiBzdHJva2Utd2lkdGg9IjIiLz48L2c+PC9zdmc+')] opacity-40" />
				<div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-dashboard-accent/20 blur-3xl" />
				<div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-dashboard-accent-light/15 blur-3xl" />

				<div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8 lg:py-20 mb-12">
					<div className="grid gap-8 lg:grid-cols-12">
						{/* Left: Company Info */}
						<div className="lg:col-span-8">
							<div className="flex items-start gap-6">
								{/* Company Logo */}
								<div className="flex-shrink-0">
									{profile.company.logoUrl ? (
										<div className="relative">
											<div className="absolute -inset-2 rounded-2xl bg-gradient-to-br from-dashboard-on-fill/20 to-dashboard-on-fill/5 blur-lg" />
											<img
												src={resolveMediaUrl(profile.company.logoUrl)}
												alt={profile.company.name}
												className="relative h-24 w-24 rounded-2xl border border-dashboard-on-fill/30 object-cover shadow-2xl sm:h-28 sm:w-28"
											/>
										</div>
									) : (
										<div className="relative">
											<div className="absolute -inset-2 rounded-2xl bg-gradient-to-br from-dashboard-on-fill/20 to-dashboard-on-fill/5 blur-lg" />
											<div className="relative flex h-24 w-24 items-center justify-center rounded-2xl border border-dashboard-on-fill/30 bg-dashboard-on-fill/10 backdrop-blur-sm sm:h-28 sm:w-28">
												<span className="text-4xl font-bold text-dashboard-on-fill sm:text-5xl">
													{profile.company.name.charAt(0).toUpperCase()}
												</span>
											</div>
										</div>
									)}
								</div>
								<div className="flex-1 pt-1">
									<div className="mb-4 flex flex-wrap items-center gap-3">
										<span className="inline-flex items-center gap-2 rounded-full bg-dashboard-accent/20 px-4 py-1.5 text-sm font-medium text-dashboard-on-photo-warm backdrop-blur-sm ring-1 ring-dashboard-on-fill/10">
											<ShieldCheck className="h-4 w-4" />
											{profile.contractor.isVerified
												? "Verified Contractor"
												: "Published Profile"}
										</span>
										{profile.company.experienceYears ? (
											<span className="inline-flex items-center gap-1.5 rounded-full bg-dashboard-on-fill/10 px-4 py-1.5 text-sm font-medium text-dashboard-on-fill/90 backdrop-blur-sm ring-1 ring-dashboard-on-fill/10">
												<Award className="h-4 w-4" />
												{profile.company.experienceYears}+ years
											</span>
										) : null}
									</div>
									<h1 className="text-3xl font-bold tracking-tight text-dashboard-on-fill sm:text-4xl lg:text-5xl xl:text-6xl">
										{profile.company.name}
									</h1>
									{profile.company.description ? (
										<p className="mt-5 max-w-2xl text-lg leading-relaxed text-dashboard-on-fill/75 sm:text-xl">
											{profile.company.description}
										</p>
									) : null}

									{/* Quick Stats */}
									<div className="mt-6 flex flex-wrap gap-6">
										<div className="flex items-center gap-2 text-dashboard-on-fill/70">
											<Users className="h-5 w-5 text-dashboard-accent-light" />
											<span className="text-sm font-medium">
												{profile.teamMembers.length} Team Members
											</span>
										</div>
										<div className="flex items-center gap-2 text-dashboard-on-fill/70">
											<Briefcase className="h-5 w-5 text-dashboard-accent-light" />
											<span className="text-sm font-medium">
												{profile.projects.length} Projects
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Right: Info Cards */}
						<div className="lg:col-span-4">
							<div className="space-y-4">
								{/* Contractor Card */}
								<div className="rounded-2xl bg-dashboard-on-fill/10 p-5 backdrop-blur-md ring-1 ring-dashboard-on-fill/10 transition-all hover:bg-dashboard-on-fill/15">
									<p className="text-xs font-semibold uppercase tracking-widest text-dashboard-on-photo-warm/80">
										Contractor
									</p>
									<div className="mt-3 flex items-center gap-4">
										{profile.contractor.profilePictureUrl ? (
											<img
												src={resolveMediaUrl(
													profile.contractor.profilePictureUrl,
												)}
												alt={profile.contractor.name}
												className="h-14 w-14 rounded-xl border border-dashboard-on-fill/20 object-cover shadow-lg"
											/>
										) : (
											<div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-dashboard-accent/30 to-dashboard-accent-light/30 text-lg font-bold text-dashboard-on-fill ring-1 ring-dashboard-on-fill/20">
												{profile.contractor.name.charAt(0).toUpperCase()}
											</div>
										)}
										<div>
											<p className="text-lg font-semibold text-dashboard-on-fill">
												{profile.contractor.name}
											</p>
											<p className="mt-0.5 text-sm text-dashboard-on-fill/60">
												{isAuthenticated() ? "Signed in" : "Viewing as visitor"}
											</p>
										</div>
									</div>
								</div>

								{/* Service Areas */}
								{profile.company.serviceAreas.length > 0 ? (
									<div className="rounded-2xl bg-dashboard-on-fill/10 p-5 backdrop-blur-md ring-1 ring-dashboard-on-fill/10">
										<p className="text-xs font-semibold uppercase tracking-widest text-dashboard-on-photo-warm/80">
											Service Areas
										</p>
										<div className="mt-3 flex flex-wrap gap-2">
											{profile.company.serviceAreas.map((area) => (
												<span
													key={`service-area-${area}`}
													className="rounded-lg bg-dashboard-on-fill/10 px-3 py-1.5 text-sm font-medium text-dashboard-on-fill/90 ring-1 ring-dashboard-on-fill/5"
												>
													{area}
												</span>
											))}
										</div>
									</div>
								) : null}
							</div>
						</div>
					</div>

					{/* Specialisations - Full Width */}
					{profile.company.specialisations.length > 0 ? (
						<div className="mt-10 border-t border-dashboard-on-fill/10 pt-8">
							<div className="flex items-center gap-3 mb-5">
								<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-dashboard-accent to-dashboard-accent-hover shadow-lg shadow-dashboard-accent/30">
									<svg
										className="h-4 w-4 text-dashboard-on-fill"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<title>Specialisations</title>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
										/>
									</svg>
								</div>
								<p className="text-sm font-bold uppercase tracking-widest text-dashboard-on-photo-warm">
									What We Do Best
								</p>
							</div>
							<div className="flex flex-wrap gap-3">
								{profile.company.specialisations.map((spec) => (
									<span
										key={`specialisation-${spec}`}
										className="relative overflow-hidden rounded-full bg-gradient-to-r from-dashboard-accent/30 via-dashboard-accent-light/25 to-dashboard-accent/30 px-6 py-2.5 text-sm font-semibold text-dashboard-on-fill backdrop-blur-sm ring-2 ring-dashboard-accent-light/40 transition-all duration-300 hover:scale-105 hover:ring-dashboard-accent-light/60 hover:shadow-lg hover:shadow-dashboard-accent/20"
									>
										<span className="relative z-10">
											{findTrade(spec)?.displayLabel ?? spec}
										</span>
										<div className="absolute inset-0 bg-gradient-to-r from-transparent via-dashboard-on-fill/10 to-transparent -translate-x-full animate-[shimmer_2s_infinite]" />
									</span>
								))}
							</div>
						</div>
					) : null}
				</div>

				{/* Wave Divider */}
				<div className="absolute -bottom-[1px] left-0 right-0">
					<svg
						viewBox="0 0 1440 120"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						className="w-full"
					>
						<title>Decorative wave</title>
						<path
							d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
							className="fill-dashboard-surface"
						/>
					</svg>
				</div>
			</div>

			{/* Content Sections */}
			<div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
				{/* Team Section */}
				{profile.teamMembers.length > 0 ? (
					<section className="mb-16">
						<div className="mb-8 flex items-center justify-between">
							<div>
								<h2 className="text-3xl font-bold tracking-tight text-dashboard-text">
									Meet Our Team
								</h2>
								<p className="mt-2 text-dashboard-muted">
									The talented professionals behind our success
								</p>
							</div>
							<div className="hidden sm:flex items-center gap-2 rounded-full bg-dashboard-warm-100 px-4 py-2 text-sm font-medium text-dashboard-accent-dark">
								<Users className="h-4 w-4" />
								{profile.teamMembers.length} Members
							</div>
						</div>
						<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
							{profile.teamMembers.map((member) => (
								<div
									key={
										member.profilePhotoUrl ||
										`${member.name}-${member.role}-${member.experienceYears ?? "unknown"}`
									}
									className="group relative overflow-hidden rounded-2xl bg-dashboard-paper shadow-sm ring-1 ring-dashboard-border-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-dashboard-border/50 hover:ring-dashboard-warm-200 dark:hover:shadow-dashboard-shadow-ink/40"
								>
									{/* Member Photo or Placeholder */}
									<div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-dashboard-divider to-dashboard-surface">
										{member.profilePhotoUrl ? (
											<img
												src={resolveMediaUrl(member.profilePhotoUrl)}
												alt={member.name}
												className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
											/>
										) : (
											<div className="flex h-full items-center justify-center bg-gradient-to-br from-dashboard-warm-100 to-dashboard-warm-400">
												<div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-dashboard-accent to-dashboard-accent-hover text-3xl font-bold text-dashboard-on-fill shadow-lg shadow-dashboard-warm-200 dark:shadow-dashboard-warm-200/40">
													{member.name.charAt(0).toUpperCase()}
												</div>
											</div>
										)}
										{/* Overlay gradient */}
										<div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
									</div>
									<div className="p-5">
										<div className="flex items-start justify-between gap-2">
											<div>
												<p className="text-lg font-semibold text-dashboard-text">
													{member.name}
												</p>
												<p className="mt-0.5 text-sm font-medium text-dashboard-accent-dark">
													{member.role}
												</p>
											</div>
											{member.experienceYears ? (
												<span className="flex items-center gap-1 rounded-full bg-dashboard-warning-bg-soft px-2.5 py-1 text-xs font-semibold text-dashboard-pill-awaiting ring-1 ring-dashboard-warning-border">
													<Star className="h-3 w-3" />
													{member.experienceYears}y
												</span>
											) : null}
										</div>
										{member.shortBio ? (
											<p className="mt-3 line-clamp-2 text-sm leading-relaxed text-dashboard-muted">
												{member.shortBio}
											</p>
										) : null}
									</div>
								</div>
							))}
						</div>
					</section>
				) : null}

				{/* Projects Section */}
				<section>
					<div className="mb-8 flex items-center justify-between">
						<div>
							<h2 className="text-3xl font-bold tracking-tight text-dashboard-text">
								Our Projects
							</h2>
							<p className="mt-2 text-dashboard-muted">
								Explore our portfolio of completed work
							</p>
						</div>
						<div className="hidden sm:flex items-center gap-2 rounded-full bg-dashboard-warm-100 px-4 py-2 text-sm font-medium text-dashboard-accent-dark">
							<Briefcase className="h-4 w-4" />
							{profile.projects.length} Projects
						</div>
					</div>
					{profile.projects.length === 0 ? (
						<div className="rounded-2xl border border-dashed border-dashboard-border p-8 text-center bg-dashboard-surface/50">
							<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-dashboard-warm-100 text-dashboard-accent-dark">
								<Briefcase className="h-6 w-6" />
							</div>
							<p className="mt-3 text-sm font-body text-dashboard-muted">
								No projects showcased yet.
							</p>
						</div>
					) : (
						<div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
							{profile.projects.map((project) => (
								<button
									type="button"
									key={project.id}
									className="group relative cursor-pointer overflow-hidden rounded-2xl bg-dashboard-paper shadow-sm ring-1 ring-dashboard-border-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-dashboard-border/50 hover:ring-dashboard-warm-200 dark:hover:shadow-dashboard-shadow-ink/40"
									onClick={() => openProject(project.id)}
								>
									<div className="relative aspect-[16/10] overflow-hidden">
										<img
											src={resolveMediaUrl(project.thumbnailUrl)}
											alt={project.title}
											className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
										/>
										{/* Overlay */}
										<div className="absolute inset-0 bg-gradient-to-t from-dashboard-scrim/80 via-dashboard-scrim/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
										<div className="absolute bottom-4 left-4 right-4 translate-y-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
											<span className="inline-flex items-center gap-2 rounded-full bg-dashboard-on-fill/20 px-4 py-2 text-sm font-medium text-dashboard-on-fill backdrop-blur-md">
												View Details
												<svg
													className="h-4 w-4"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<title>View details</title>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M9 5l7 7-7 7"
													/>
												</svg>
											</span>
										</div>
									</div>
									<div className="p-6">
										<p className="text-xl font-semibold text-dashboard-text transition-colors group-hover:text-dashboard-accent-dark">
											{project.title}
										</p>
										<p className="mt-2 line-clamp-2 text-sm leading-relaxed text-dashboard-muted">
											{project.shortDescription}
										</p>
										<div className="mt-4 flex items-center text-sm font-semibold text-dashboard-accent-dark">
											View project
											<svg
												className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
											>
												<title>Open project</title>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M9 5l7 7-7 7"
												/>
											</svg>
										</div>
									</div>
								</button>
							))}
						</div>
					)}
				</section>

				{selected ? (
					<ProjectDetailsModal
						data={selected}
						onClose={closeModal}
						onRequireAuth={requireAuth}
					/>
				) : null}
			</div>
		</div>
	);
}
